import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LiveChart from './LiveChart';
import TxAdditionalInfoPopover from './TxAdditionalInfoPopover';
import { FiAlertCircle, FiClock, FiEye, FiHelpCircle, FiArrowUpRight } from 'react-icons/fi';
import CopyButton from './transaction/CopyButton';
import { useRecentAllTransactions } from '../hooks/useRecentAllTransactions';

function DashboardPanels() {
  const [addInfo, setAddInfo] = useState(null);
  const [tpsRange, setTpsRange] = useState('30m');
  const [pingRange, setPingRange] = useState('30m');
  const { transactions, loading, error } = useRecentAllTransactions();

  const closeAddInfo = useCallback(() => setAddInfo(null), []);

  useEffect(() => {
    if (!addInfo) {
      return undefined;
    }
    const onDocMouseDown = (event) => {
      if (event.target.closest?.('.tx-addinfo-popover')) {
        return;
      }
      if (event.target.closest?.('.tx-eye')) {
        return;
      }
      closeAddInfo();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeAddInfo();
      }
    };
    const onWinScroll = () => closeAddInfo();
    window.addEventListener('scroll', onWinScroll, { passive: true });
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onWinScroll);
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [addInfo, closeAddInfo]);

  const handleEyeClick = (event, tx) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAddInfo((current) =>
      current?.signature === tx.signature ? null : { tx, anchor: rect },
    );
  };

  return (
    <>
      <section className="panels-grid">
        <article className="panel panel-full panel-latest-tx">
          <div className="panel-head">
            <h3>Latest Transactions</h3>
            <button type="button" className="customize-btn">
              Customize
            </button>
          </div>

          <div className="tx-table-scroll">
            {loading ? (
              <div className="tx-table">
                <div className="tx-row tx-head">
                  <span className="tx-leading-col"><FiHelpCircle /></span>
                  <span>Signature</span>
                  <span className="tx-time-head">Time <FiClock /></span>
                  <span>Block</span>
                  <span>Action</span>
                </div>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="tx-row tx-skeleton-row" aria-hidden>
                    <span className="tx-leading-col"><span className="tx-skeleton tx-skeleton--circle" /></span>
                    <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--wide" /></span>
                    <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--med" /></span>
                    <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--short" /></span>
                    <span className="tx-skeleton-cell"><span className="tx-skeleton tx-skeleton--med" /></span>
                  </div>
                ))}
              </div>
            ) : null}

            {error && !loading ? (
              <p className="tx-table-error" role="alert">{error}</p>
            ) : null}

            {!loading && !error ? (
              <div className="tx-table">
                <div className="tx-row tx-head">
                  <span className="tx-leading-col"><FiHelpCircle /></span>
                  <span>Signature</span>
                  <span className="tx-time-head">Time <FiClock /></span>
                  <span>Block</span>
                  <span>Action</span>
                </div>
                {transactions.length === 0 ? (
                  <p className="tx-table-empty">No recent transactions found.</p>
                ) : (
                  transactions.map((tx) => (
                    <div key={tx.signature} className="tx-row">
                      <span className="tx-leading-col">
                        <button
                          type="button"
                          className={`tx-eye ${addInfo?.tx?.signature === tx.signature ? 'is-active' : ''}`}
                          aria-expanded={addInfo?.tx?.signature === tx.signature}
                          aria-haspopup="dialog"
                          aria-label="Additional transaction info"
                          onClick={(event) => handleEyeClick(event, tx)}
                        >
                          <FiEye />
                        </button>
                      </span>
                      <span className="tx-signature-cell">
                        {!tx.success ? <FiAlertCircle className="tx-alert-dot" /> : null}
                        <Link to={`/tx/${encodeURIComponent(tx.signature)}`} className="tx-signature-link">
                          {tx.displaySig}
                        </Link>
                        <CopyButton text={tx.signature} className="tx-copy-icon-btn" label="Copy signature" />
                      </span>
                      <span>{tx.time}</span>
                      <Link to={`/tx/${tx.block}`} className="tx-block-link">
                        {tx.block}
                      </Link>
                      <span className="tx-action-cell">
                        <span className="tx-action-pill">
                          <FiArrowUpRight />
                          {tx.action}
                        </span>
                        {tx.badge ? <span className="tx-plus-pill">{tx.badge}</span> : null}
                      </span>
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </div>
          <Link to="/transactions" className="view-all-link">
            VIEW ALL TRANSACTIONS <FiArrowUpRight />
          </Link>
        </article>

        <article className="panel panel-full">
          <div className="panel-head chart-head">
            <h3>TPS | True TPS</h3>
            <div className="chart-range-switch" role="tablist" aria-label="TPS chart range">
              {['30m', '2H', '6H'].map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tpsRange === id}
                  className={tpsRange === id ? 'is-active' : ''}
                  onClick={() => setTpsRange(id)}
                >
                  {id}
                </button>
              ))}
            </div>
          </div>
          <LiveChart variant="tps" range={tpsRange} />
        </article>

        <article className="panel panel-full">
          <div className="panel-head chart-head">
            <h3>Average Ping Time(Network response time)</h3>
            <div className="chart-range-switch" role="tablist" aria-label="Ping chart range">
              {['30m', '2H', '6H'].map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={pingRange === id}
                  className={pingRange === id ? 'is-active' : ''}
                  onClick={() => setPingRange(id)}
                >
                  {id}
                </button>
              ))}
            </div>
          </div>
          <LiveChart variant="ping" range={pingRange} />
        </article>
      </section>

      {addInfo ? (
        <TxAdditionalInfoPopover
          key={addInfo.tx.signature}
          anchor={addInfo.anchor}
          signature={addInfo.tx.signature}
          tx={addInfo.tx}
          onClose={closeAddInfo}
        />
      ) : null}
    </>
  );
}

export default DashboardPanels;
